import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

// Human-friendly section names shown in the admin list.
const META = {
  hero:                    "Hero",
  the_day:                 "The Day",
  numbers:                 "The Numbers",
  after_the_run:           "After the Run",
  food:                    "Food · Copenhagen meets Ávila",
  auction:                 "Auction",
  why_we_ran:              "Why We Ran",
  we_didnt_do_this_alone:  "We didn't do this alone",
  institutional:           "Institutional Support",
  food_collaborators:      "Food & Hospitality collaborators",
  prizes_support:          "Prizes & Support",
  volunteers:              "Volunteers",
  ambassadors:             "Ambassadors",
  transparency:            "Result · Transparency",
  what_we_created:         "What We Created",
  next_chapter:            "Next Chapter · KB Hallen",
  final_cta:               "Final CTA",
};

export default function SocialRunAdmin({ authed }) {
  const [sections, setSections] = useState(null);
  const [meta, setMeta] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const [analysis, setAnalysis] = useState({});           // { [id]: flags }
  const [selected, setSelected] = useState(new Set());
  const [publishModal, setPublishModal] = useState(null); // { scope, dry }

  const load = async () => {
    const [rSec, rMeta, rC, rA, rAna] = await Promise.all([
      authed.get("/content?kind=social_run_section&status="),
      authed.get("/admin/social-run/meta"),
      authed.get("/content?kind=collaborator&status=published"),
      authed.get("/ambassadors"),
      authed.get("/admin/social-run/analysis"),
    ]);
    const rows = (rSec.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
    setMeta(rMeta.data || {});
    setCollabs(rC.data || []);
    setAmbassadors(rA.data.ambassadors || []);
    const byId = {};
    (rAna.data.sections || []).forEach(s => { byId[s.id] = s; });
    setAnalysis(byId);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    const reord = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, data: { ...s.data, order: i + 1 } }));
    setSections(reord);
    await authed.post("/admin/social-run/reorder", { order: reord.map((s, i) => ({ id: s.id, order: i + 1 })) });
  };

  const setState = async (next) => {
    const cur = meta?.recap_state || (meta?.recap_enabled ? "live" : "draft");
    if (next === cur) return;
    const prompts = {
      draft: "Revert to DRAFT? The public page returns to the LEGACY layout. No content is deleted.",
      ready: "Mark recap as READY? Public still sees the LEGACY layout. You can share admin previews.",
      live:  "Set recap LIVE? /events/social-run-for-spain will render the NEW recap for every visitor.",
    };
    if (!window.confirm(prompts[next])) return;
    const r = await authed.put("/admin/social-run/meta", { recap_state: next });
    setMeta(r.data);
    const labels = { draft: "DRAFT — legacy layout restored.", ready: "READY — legacy stays public, preview enabled.", live: "LIVE — new recap is public." };
    setMsg(labels[next]);
    setTimeout(() => setMsg(""), 3500);
  };

  const seedMissing = async () => {
    if (!window.confirm("Insert the 17 default draft sections? No-op if any section already exists.")) return;
    const r = await authed.post("/admin/social-run/seed-missing");
    setMsg(r.data.inserted > 0 ? `Seeded ${r.data.inserted} draft sections.` : "Already seeded — nothing to do.");
    await load();
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openPublishModal = async (scope) => {
    // scope = 'all' | 'selected'
    const ids = scope === "selected" ? Array.from(selected) : null;
    const r = await authed.post("/admin/social-run/publish-batch", { ids, dry_run: true });
    setPublishModal({ scope, ids, dry: r.data });
  };

  const confirmPublish = async () => {
    const { scope, ids } = publishModal;
    setPublishModal({ ...publishModal, busy: true });
    const r = await authed.post("/admin/social-run/publish-batch", { ids: scope === "selected" ? ids : null });
    setPublishModal({ ...publishModal, result: r.data });
    setSelected(new Set());
    await load();
  };

  if (sections === null) return <div className="text-ink/50 text-sm">Loading…</div>;

  const publishedCount = sections.filter(s => s.status === "published" && (s.data?.visible !== false)).length;
  const draftPendingCount = sections.filter(s => !!s.data_draft).length;
  const state = meta?.recap_state || (meta?.recap_enabled ? "live" : "draft");
  const stateColor = { draft: "bg-line/30 text-ink/70", ready: "bg-amber-100 text-amber-800", live: "bg-emerald-100 text-emerald-800" }[state];
  const stateCopy = {
    draft: "Public visitors see the LEGACY layout. Curate every section, then advance to READY.",
    ready: "Public still sees LEGACY. Share the admin preview below with reviewers before going LIVE.",
    live:  "Public visitors see the NEW recap. Reverting to DRAFT restores the legacy layout without deleting content.",
  }[state];

  return (
    <div className="space-y-6" data-testid="social-run-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="label-mono text-terracotta text-[10px]">EVENTS · SOCIAL RUN FOR SPAIN</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Editorial recap</h2>
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="recap-state-badge" className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${stateColor}`}>{state.toUpperCase()}</span>
              <a href="/events/social-run-for-spain?preview=1" target="_blank" rel="noreferrer"
                 data-testid="recap-preview-link"
                 className="text-[11px] underline text-ink/70 hover:text-ink">Open admin preview ↗</a>
            </div>
            <p className="mt-3 text-xs text-ink/60 max-w-xl">{stateCopy}</p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[260px]">
            <div className="text-[11px] text-ink/60">
              <span className="text-emerald-700 font-medium">{publishedCount}</span> published
              {" · "}
              <span className="text-amber-700 font-medium">{draftPendingCount}</span> with pending drafts
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setState("draft")} disabled={state === "draft"} data-testid="recap-state-draft"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${state === "draft" ? "border-line text-ink/40 bg-line/10 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
                {state === "live" ? "Revert to legacy" : "Set draft"}
              </button>
              <button onClick={() => setState("ready")} disabled={state === "ready"} data-testid="recap-state-ready"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "ready" ? "bg-amber-100 text-amber-800 cursor-default" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
                Mark ready
              </button>
              <button onClick={() => setState("live")} disabled={state === "live"} data-testid="recap-state-live"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "live" ? "bg-emerald-600 text-white cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                Set recap live
              </button>
            </div>
            {sections.length === 0 && (
              <button onClick={seedMissing} data-testid="recap-seed-missing"
                      className="mt-1 px-3 py-2 text-[11px] uppercase tracking-widest border border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
                Seed 17 default drafts
              </button>
            )}
            {msg && <div className="text-[11px] text-ink/60">{msg}</div>}
          </div>
        </div>
      </section>

      {/* Bulk publish toolbar — publishing is intentionally separate from */}
      {/* going LIVE. Publishing writes status=published on each section;   */}
      {/* recap_state is untouched, so the public keeps seeing legacy      */}
      {/* until the admin explicitly flips SET RECAP LIVE.                 */}
      <section className="border border-line rounded-xl p-4 bg-white flex flex-wrap items-center gap-3">
        <div className="text-[11px] text-ink/60 flex-1 min-w-[220px]">
          <span className="font-medium text-ink">Bulk publish</span> · publishes section content only — does not change recap state.
          {selected.size > 0 && <span className="ml-2 text-terracotta">{selected.size} selected</span>}
        </div>
        <button onClick={() => openPublishModal("selected")} disabled={selected.size === 0}
                data-testid="recap-publish-selected"
                className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${selected.size === 0 ? "border-line text-ink/30 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
          Publish selected
        </button>
        <button onClick={() => openPublishModal("all")}
                data-testid="recap-publish-all"
                className="px-3 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">
          Publish all sections
        </button>
      </section>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="sr-sections-list">
            {sections.map((s, idx) => (
              <SortableRow key={s.id} s={s} idx={idx}
                           onEdit={() => setEditing(s)}
                           analysis={analysis[s.id]}
                           checked={selected.has(s.id)}
                           onToggleCheck={() => toggleSelected(s.id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editing && (
        <SectionEditor s={editing} authed={authed} collabs={collabs} ambassadors={ambassadors}
                       onClose={() => { setEditing(null); load(); }} />
      )}

      {publishModal && (
        <PublishBatchModal modal={publishModal} onConfirm={confirmPublish} onClose={() => setPublishModal(null)} />
      )}
    </div>
  );
}

function SortableRow({ s, idx, onEdit, analysis, checked, onToggleCheck }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published" || d.visible === false;
  const hasDraft = !!s.data_draft;
  const name = META[d.slug] || d.slug;
  const a = analysis || {};
  const missingLangs = a.missing_translation || [];
  return (
    <div ref={setNodeRef} style={style} data-testid={`sr-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <input type="checkbox" checked={!!checked} onChange={onToggleCheck}
             data-testid={`sr-check-${d.slug}`}
             className="w-4 h-4 cursor-pointer accent-terracotta" onClick={(e) => e.stopPropagation()} />
      <button {...attributes} {...listeners} className="cursor-grab text-ink/30 hover:text-ink text-xl px-2">≡</button>
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{idx + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" />
               : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base truncate">{name}</div>
        <div className="text-[11px] text-ink/50 truncate">{d.template} · {(d.headline?.en || d.headline?.da || "").slice(0, 60)}</div>
        {(a.missing_image || a.missing_headline || missingLangs.length > 0) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {a.missing_headline && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">NO HEADLINE</span>}
            {a.missing_image && <span data-testid={`sr-flag-image-${d.slug}`} className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">MISSING IMAGE</span>}
            {missingLangs.map(l => (
              <span key={l} data-testid={`sr-flag-tx-${l}-${d.slug}`} className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">MISSING {l.toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && <span className="text-[9px] label-mono tracking-widest px-2 py-1 rounded bg-amber-100 text-amber-800">DRAFT</span>}
        <span className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
          {hidden ? (s.status === "published" ? "HIDDEN" : "DRAFT") : "PUBLISHED"}
        </span>
        <button onClick={onEdit} data-testid={`sr-edit-${d.slug}`}
                className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
      </div>
    </div>
  );
}

// ─── Bulk publish modal ───────────────────────────────────────────────
function PublishBatchModal({ modal, onConfirm, onClose }) {
  const { dry, result, busy, scope } = modal;
  const totalTx = (dry?.missing_translation_by_lang?.en || 0)
                + (dry?.missing_translation_by_lang?.da || 0)
                + (dry?.missing_translation_by_lang?.es || 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" data-testid="recap-publish-modal">
      <div className="bg-white max-w-lg w-full rounded-xl border border-line shadow-xl">
        <div className="p-6 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">SOCIAL RUN · BULK PUBLISH</div>
          <h3 className="font-editorial text-2xl tracking-tighter mt-1">
            {result ? "Done." : (scope === "selected" ? "Publish selected sections" : "Publish all sections")}
          </h3>
          <p className="text-xs text-ink/60 mt-2">
            {result
              ? "The public page still shows the LEGACY layout because recap state is unchanged. Flip SET RECAP LIVE separately when you're ready."
              : "This publishes section content only. Recap state (DRAFT/READY/LIVE) is NOT changed, so public visitors continue seeing the legacy page until you flip SET RECAP LIVE separately."}
          </p>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {!result && dry && (
            <>
              <Row label="Sections to publish" value={dry.to_publish} testid="modal-to-publish" />
              <Row label="Already published (skipped)" value={dry.already_published} muted />
              <Row label="Hidden (visible = false)" value={dry.hidden} warn={dry.hidden > 0} testid="modal-hidden" />
              <Row label="Missing headline" value={dry.missing_headline} warn={dry.missing_headline > 0} testid="modal-missing-headline" />
              <Row label="Missing image (template requires one)" value={dry.missing_image} warn={dry.missing_image > 0} testid="modal-missing-image" />
              <div className="pt-2 border-t border-line/50">
                <div className="text-[11px] text-ink/60 mb-1">Translation gaps (not blocking)</div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniRow lang="EN" n={dry.missing_translation_by_lang.en} />
                  <MiniRow lang="DA" n={dry.missing_translation_by_lang.da} />
                  <MiniRow lang="ES" n={dry.missing_translation_by_lang.es} />
                </div>
              </div>
              {totalTx > 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Some sections are missing translations. You can still publish — the fallback locale will be shown until you translate.
                </div>
              )}
            </>
          )}
          {result && (
            <>
              <Row label="Sections published" value={result.published_count} testid="modal-published-count" />
              <Row label="Hidden after publish" value={result.hidden} muted />
              <Row label="Sections with missing headline" value={result.missing_headline} muted />
              <Row label="Sections with missing image" value={result.missing_image} muted />
            </>
          )}
        </div>
        <div className="p-4 border-t border-line flex justify-end gap-2">
          {!result ? (
            <>
              <button onClick={onClose} data-testid="modal-cancel"
                      className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">
                Cancel
              </button>
              <button onClick={onConfirm} disabled={busy || (dry?.to_publish || 0) === 0}
                      data-testid="modal-confirm"
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest ${(dry?.to_publish || 0) === 0 ? "bg-line text-ink/40 cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                {busy ? "Publishing…" : `Confirm publish (${dry?.to_publish || 0})`}
              </button>
            </>
          ) : (
            <button onClick={onClose} data-testid="modal-close"
                    className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, warn, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={muted ? "text-ink/50" : "text-ink/80"}>{label}</span>
      <span className={`font-editorial text-lg tabular-nums ${warn ? "text-rose-700" : muted ? "text-ink/40" : "text-ink"}`}>{value}</span>
    </div>
  );
}

function MiniRow({ lang, n }) {
  return (
    <div className={`flex flex-col items-center py-2 rounded ${n > 0 ? "bg-amber-50 text-amber-800" : "bg-line/10 text-ink/40"}`}>
      <span className="font-editorial text-xl tabular-nums">{n}</span>
      <span className="text-[10px] label-mono tracking-widest">{lang}</span>
    </div>
  );
}

// ─── Section editor (compact, reuses ImagePicker + resolveImageUrl) ─────
function SectionEditor({ s, authed, collabs, ambassadors, onClose }) {
  const startFromDraft = !!s.data_draft;
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(s.data_draft || s.data || {})));
  const [saving, setSaving] = useState("");
  const [pickerFor, setPickerFor] = useState(null);
  const [lang, setLang] = useState("en");
  const [pane, setPane] = useState("content");

  const patch = (k, v) => setDraft(x => ({ ...x, [k]: v }));
  const patchLang = (k, v) => setDraft(x => ({ ...x, [k]: { ...(x[k] || {}), [lang]: v } }));

  const saveDraft = async () => {
    setSaving("Saving draft…");
    await authed.put(`/content/${s.id}/draft`, { data: draft });
    setSaving("Draft saved.");
    setTimeout(() => setSaving(""), 1400);
  };
  const publish = async () => {
    if (!window.confirm("Publish this section internally? (Still won't appear on the public site until the master recap toggle is on.)")) return;
    setSaving("Publishing…");
    await authed.post(`/content/${s.id}/publish`, { data: draft });
    setSaving("Published.");
    setTimeout(onClose, 600);
  };
  const discardDraft = async () => {
    if (!window.confirm("Discard draft changes?")) return;
    await authed.post(`/content/${s.id}/discard-draft`);
    onClose();
  };
  const toggleVisible = () => patch("visible", draft.visible === false ? true : false);

  const setImage = (idx, url, alt = "") => {
    const images = [...(draft.images || [])];
    images[idx] = { ...(images[idx] || {}), url, alt: images[idx]?.alt || { en: alt, da: alt, es: alt } };
    patch("images", images);
    setPickerFor(null);
  };
  const setFocal = (idx, kind, xy) => {
    const images = [...(draft.images || [])];
    images[idx] = { ...(images[idx] || {}), [kind]: xy };
    patch("images", images);
  };
  const setAlt = (idx, v) => {
    const images = [...(draft.images || [])];
    images[idx] = { ...(images[idx] || {}), alt: { ...(images[idx].alt || {}), [lang]: v } };
    patch("images", images);
  };

  const template = draft.template || "editorial_text";
  const hasImages = ["hero", "editorial_grid", "split_image", "full_bleed_quote", "final_cta"].includes(template);
  const maxImages = template === "editorial_grid" ? 6 : template === "hero" ? 1 : template === "split_image" ? 2 : 1;
  const isCollabs = template === "collaborators";
  const isAmbassadors = template === "ambassadors";
  const isStats = template === "stats";

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-start justify-center overflow-y-auto p-4" data-testid={`sr-editor-${draft.slug}`}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl my-6">
        <div className="p-5 border-b border-line flex items-baseline justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="label-mono text-terracotta text-[10px]">{startFromDraft ? "EDITING DRAFT" : "EDIT SECTION"}</div>
            <h3 className="font-editorial text-2xl tracking-tighter mt-1">{META[draft.slug] || draft.slug}</h3>
            <div className="text-[11px] text-ink/50 mt-0.5">Template: {template}</div>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink text-2xl px-2" data-testid="sr-editor-close">×</button>
        </div>

        <div className="px-5 pt-3 flex gap-2 border-b border-line items-center">
          {[["content", "Content"], ["media", "Media"]].map(([k, l]) => (
            <button key={k} onClick={() => setPane(k)} data-testid={`sr-editor-pane-${k}`}
                    className={`text-xs uppercase tracking-widest px-3 py-2 ${pane === k ? "border-b-2 border-terracotta text-terracotta -mb-px" : "text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-ink/60 flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={draft.visible !== false} onChange={toggleVisible} data-testid="sr-visible-toggle" />
              Show on page
            </label>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
          {pane === "content" && (
            <>
              <div className="flex items-center gap-2">
                <div className="label-mono text-ink/60 text-[10px]">LANGUAGE</div>
                {["en", "da", "es"].map(l => (
                  <button key={l} onClick={() => setLang(l)} data-testid={`sr-lang-${l}`}
                          className={`text-[10px] label-mono px-3 py-1 rounded ${lang === l ? "bg-ink text-white" : "bg-line/30 text-ink/60"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
              <Field label="Eyebrow" value={(draft.eyebrow || {})[lang] || ""} onChange={v => patchLang("eyebrow", v)} />
              {"meta" in draft && <Field label="Meta line (hero)" value={(draft.meta || {})[lang] || ""} onChange={v => patchLang("meta", v)} />}
              <Field label="Headline" value={(draft.headline || {})[lang] || ""} onChange={v => patchLang("headline", v)} multiline />
              <Field label="Body" value={(draft.body || {})[lang] || ""} onChange={v => patchLang("body", v)} multiline rows={7} />

              <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-line/50">
                <div>
                  <div className="label-mono text-ink/60 text-[10px] mb-1">Primary CTA label</div>
                  <input value={(draft.cta_label || {})[lang] || ""} onChange={e => patchLang("cta_label", e.target.value)}
                         className="w-full border border-line px-3 py-2 text-sm" />
                  <input value={draft.cta_url || ""} onChange={e => patch("cta_url", e.target.value)}
                         placeholder="URL" className="mt-2 w-full border border-line px-3 py-2 text-sm" />
                </div>
                <div>
                  <div className="label-mono text-ink/60 text-[10px] mb-1">Secondary CTA label</div>
                  <input value={(draft.cta2_label || {})[lang] || ""} onChange={e => patchLang("cta2_label", e.target.value)}
                         className="w-full border border-line px-3 py-2 text-sm" />
                  <input value={draft.cta2_url || ""} onChange={e => patch("cta2_url", e.target.value)}
                         placeholder="URL" className="mt-2 w-full border border-line px-3 py-2 text-sm" />
                </div>
              </div>

              {isStats && <StatsEditor draft={draft} patch={patch} lang={lang} />}
              {isCollabs && <CollabPicker draft={draft} patch={patch} collabs={collabs} />}
              {isAmbassadors && <AmbassadorPicker draft={draft} patch={patch} ambassadors={ambassadors} />}

              {template === "hero" && (
                <div className="pt-4 border-t border-line/50">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="sr-hero-show-headline">
                    <input type="checkbox" checked={draft.show_headline !== false}
                           onChange={e => patch("show_headline", e.target.checked)}
                           className="mt-1 accent-terracotta" />
                    <div>
                      <div className="text-sm font-medium">Show hero headline over the photograph</div>
                      <div className="text-[11px] text-ink/50 mt-0.5">When off, the photograph, eyebrow and body stay; only the large title is hidden. CMS content is preserved.</div>
                    </div>
                  </label>
                </div>
              )}
            </>
          )}

          {pane === "media" && (
            hasImages ? (
              <div className="space-y-4">
                {Array.from({ length: maxImages }).map((_, idx) => (
                  <ImageSlot key={idx} idx={idx} image={draft.images?.[idx]}
                             onReplace={() => setPickerFor(idx)}
                             onRemove={() => setImage(idx, "")}
                             onFocal={(k, xy) => setFocal(idx, k, xy)}
                             onAlt={v => setAlt(idx, v)}
                             lang={lang}
                             mobile={idx === 0 && (template === "hero" || template === "full_bleed_quote" || template === "final_cta")} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-ink/60 border border-dashed border-line rounded p-6">This template has no photo slots. Edit collaborator selection / stats in Content instead.</div>
            )
          )}
        </div>

        <div className="p-5 border-t border-line flex justify-between gap-3 sticky bottom-0 bg-white items-center">
          <div className="flex gap-3 items-center">
            <button onClick={onClose} className="text-ink/60 hover:text-ink px-4 py-2 text-sm uppercase tracking-widest">Cancel</button>
            {startFromDraft && <button onClick={discardDraft} className="text-red-600/80 hover:text-red-600 px-3 py-2 text-xs uppercase tracking-widest">Discard draft</button>}
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-[11px] text-ink/50">{saving}</span>}
            <button onClick={saveDraft} data-testid="sr-save-draft"
                    className="border border-ink text-ink px-5 py-2 text-sm uppercase tracking-widest hover:bg-ink hover:text-white">Save draft</button>
            <button onClick={publish} data-testid="sr-publish"
                    className="bg-terracotta hover:bg-terracotta-dark text-white px-5 py-2 text-sm uppercase tracking-widest">Publish</button>
          </div>
        </div>
      </div>

      {pickerFor !== null && (
        <ImagePicker authed={authed}
                     onPick={(a) => setImage(pickerFor, a.url, a.alt_text)}
                     onClose={() => setPickerFor(null)} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, multiline, rows = 3 }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-1">{label}</div>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} className="w-full border border-line px-3 py-2 text-sm" />
        : <input value={value} onChange={e => onChange(e.target.value)} className="w-full border border-line px-3 py-2 text-sm" />}
    </div>
  );
}

function ImageSlot({ idx, image, onReplace, onRemove, onFocal, onAlt, lang, mobile }) {
  const url = image?.url;
  const focal = image?.focal || { x: 50, y: 50 };
  const focalMobile = image?.focal_mobile || focal;
  const positions = [[0, 0], [50, 0], [100, 0], [0, 50], [50, 50], [100, 50], [0, 100], [50, 100], [100, 100]];
  const active = (f, x, y) => Math.abs((f.x || 50) - x) < 8 && Math.abs((f.y || 50) - y) < 8;
  return (
    <div className="border border-line rounded-lg p-4 flex items-start gap-4">
      <div className="w-40 shrink-0">
        <div className="text-[10px] label-mono text-ink/50 mb-2">Photo {idx + 1}</div>
        <div className="aspect-[4/3] bg-line/10 border border-line/50 overflow-hidden">
          {url ? <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
               : <div className="w-full h-full flex items-center justify-center text-ink/30 text-xs">No photo</div>}
        </div>
        <div className="mt-2 flex gap-1 flex-wrap">
          <button onClick={onReplace} className="text-[10px] uppercase tracking-widest border border-line px-2 py-1 hover:border-ink">{url ? "Replace" : "Choose"}</button>
          {url && <button onClick={onRemove} className="text-[10px] uppercase tracking-widest border border-line/60 px-2 py-1 text-ink/60 hover:text-red-600">Remove</button>}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        {url && (
          <>
            <div className="mb-3 flex flex-wrap gap-4">
              <div>
                <div className="text-[10px] label-mono text-ink/50 mb-1">Focus (desktop)</div>
                <div className="grid grid-cols-3 gap-1 w-24">
                  {positions.map(([x, y]) => <button key={`d-${x}-${y}`} onClick={() => onFocal("focal", { x, y })}
                                                    className={`aspect-square border ${active(focal, x, y) ? "bg-terracotta border-terracotta" : "border-line hover:border-ink"} rounded-sm`} />)}
                </div>
              </div>
              {mobile && (
                <div>
                  <div className="text-[10px] label-mono text-ink/50 mb-1">Focus (mobile)</div>
                  <div className="grid grid-cols-3 gap-1 w-24">
                    {positions.map(([x, y]) => <button key={`m-${x}-${y}`} onClick={() => onFocal("focal_mobile", { x, y })}
                                                      className={`aspect-square border ${active(focalMobile, x, y) ? "bg-terracotta border-terracotta" : "border-line hover:border-ink"} rounded-sm`} />)}
                  </div>
                </div>
              )}
            </div>
            <input value={image?.alt?.[lang] || ""} onChange={e => onAlt(e.target.value)}
                   placeholder={`Alt (${lang.toUpperCase()})`}
                   className="w-full border border-line px-2 py-1 text-xs" />
          </>
        )}
      </div>
    </div>
  );
}

function StatsEditor({ draft, patch, lang }) {
  const stats = draft.stats || [];
  return (
    <div className="pt-2 border-t border-line/50">
      <div className="label-mono text-ink/60 text-[10px] mb-2">Statistics</div>
      {stats.map((st, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
          <input value={st.value} onChange={e => { const s = [...stats]; s[i] = { ...s[i], value: e.target.value }; patch("stats", s); }}
                 placeholder="Value" className="col-span-4 border border-line px-2 py-1.5 text-sm" />
          <input value={st.label?.[lang] || ""}
                 onChange={e => { const s = [...stats]; s[i] = { ...s[i], label: { ...(s[i].label || {}), [lang]: e.target.value } }; patch("stats", s); }}
                 placeholder={`Label (${lang.toUpperCase()})`}
                 className="col-span-7 border border-line px-2 py-1.5 text-sm" />
          <button onClick={() => { const s = [...stats]; s.splice(i, 1); patch("stats", s); }} className="text-ink/40 hover:text-red-600 text-lg">×</button>
        </div>
      ))}
      <button onClick={() => patch("stats", [...stats, { value: "", label: { da: "", en: "", es: "" } }])}
              className="text-xs uppercase tracking-widest border border-line px-3 py-1.5 hover:border-ink">+ Add stat</button>
    </div>
  );
}

function CollabPicker({ draft, patch, collabs }) {
  const chosen = new Set(draft.collaborator_slugs || []);
  const toggle = (slug) => {
    const next = new Set(chosen);
    next.has(slug) ? next.delete(slug) : next.add(slug);
    patch("collaborator_slugs", Array.from(next));
  };
  return (
    <div className="pt-2 border-t border-line/50">
      <div className="label-mono text-ink/60 text-[10px] mb-2">Collaborators to show in this section</div>
      <div className="mb-3 flex items-center gap-3 text-[11px] text-ink/60">
        <span>Fallback tier when nothing is selected:</span>
        <select value={draft.collaborator_fallback_tier || ""}
                onChange={e => patch("collaborator_fallback_tier", e.target.value || null)}
                className="border border-line px-2 py-1 text-xs">
          <option value="">— none —</option>
          <option value="main">main</option>
          <option value="supporter">supporter</option>
          <option value="friend">friend</option>
        </select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto">
        {collabs.map(c => {
          const slug = c.data?.slug || c.id;
          const sel = chosen.has(slug);
          return (
            <button key={c.id} onClick={() => toggle(slug)} data-testid={`sr-collab-pick-${slug}`}
                    className={`border ${sel ? "border-terracotta bg-terracotta/5" : "border-line"} rounded p-2 flex items-center gap-2 text-left hover:border-ink`}>
              {c.data?.logo ? <img src={resolveImageUrl(c.data.logo)} alt="" className="w-8 h-8 object-contain" />
                            : <div className="w-8 h-8 bg-line/30 rounded" />}
              <div className="text-[11px] truncate flex-1">{c.data?.name || slug}</div>
              {sel && <span className="text-terracotta text-[10px] label-mono">✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AmbassadorPicker({ draft, patch, ambassadors }) {
  const chosen = new Set(draft.ambassador_slugs || []);
  const toggle = (slug) => {
    const next = new Set(chosen);
    next.has(slug) ? next.delete(slug) : next.add(slug);
    patch("ambassador_slugs", Array.from(next));
  };
  if (ambassadors.length === 0) {
    return (
      <div className="pt-2 border-t border-line/50 text-[12px] text-ink/60">
        No ambassadors created yet. Add ambassadors under Content › kind="ambassador" (or ask an admin to expose the dedicated tab). When empty, all published ambassadors will be shown by default.
      </div>
    );
  }
  return (
    <div className="pt-2 border-t border-line/50">
      <div className="label-mono text-ink/60 text-[10px] mb-2">Ambassadors to show</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ambassadors.map(a => (
          <button key={a.slug} onClick={() => toggle(a.slug)}
                  className={`border ${chosen.has(a.slug) ? "border-terracotta bg-terracotta/5" : "border-line"} rounded p-2 flex items-center gap-2 text-left`}>
            {a.photo ? <img src={resolveImageUrl(a.photo)} alt="" className="w-8 h-8 object-cover rounded" /> : <div className="w-8 h-8 bg-line/30 rounded" />}
            <div className="text-[11px] truncate flex-1">{a.name}</div>
            {chosen.has(a.slug) && <span className="text-terracotta text-[10px]">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
